"use client";

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import {
    SectionShell,
    EditorialTitle,
    GradientText,
    RevealOnScroll,
} from "@/components/ui/Socials/JoinInstructors/_primitives";

const CATEGORIES = [
    { id: "code", icon: "💻" },
    { id: "design", icon: "🎨" },
    { id: "marketing", icon: "📣" },
    { id: "career", icon: "🎯" },
    { id: "finance", icon: "📊" },
    { id: "strategy", icon: "🧭" },
    { id: "product", icon: "🧩" },
    { id: "ops", icon: "⚙️" },
    { id: "data", icon: "📈" },
    { id: "language", icon: "🌍" },
    { id: "writing", icon: "✍️" },
    { id: "other", icon: "✨" },
];

export default function ExpertiseSection() {
    const t = useTranslations();

    return (
        <SectionShell label={t("joinMentor_expertise_marker")}>
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
                <RevealOnScroll>
                    <p className="text-[11px] uppercase tracking-[0.28em] text-primary mb-6">
                        {t("joinMentor_expertise_eyebrow")}
                    </p>
                    <EditorialTitle className="text-4xl sm:text-5xl md:text-6xl">
                        {t("joinMentor_expertise_title_part1")}{" "}
                        <GradientText>
                            {t("joinMentor_expertise_title_emphasis")}
                        </GradientText>
                    </EditorialTitle>
                    <p
                        className="mt-6 sm:mt-8 text-base sm:text-lg text-gray-600 dark:text-white/70 italic"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                        {t("joinMentor_expertise_subtitle_part1")}{" "}
                        <span className="text-gray-900 dark:text-white not-italic font-semibold">
                            {t("joinMentor_expertise_subtitle_emphasis")}
                        </span>
                        {t("joinMentor_expertise_subtitle_part2")}
                    </p>
                </RevealOnScroll>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {CATEGORIES.map((cat, i) => (
                    <motion.div
                        key={cat.id}
                        initial={{ opacity: 0, y: 16, scale: 0.96 }}
                        whileInView={{ opacity: 1, y: 0, scale: 1 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{
                            duration: 0.5,
                            delay: i * 0.04,
                            ease: [0.16, 1, 0.3, 1],
                        }}
                        className="group rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] p-5 sm:p-6 flex flex-col items-start gap-3 hover:border-primary/40 hover:bg-white dark:hover:bg-white/[0.05] transition-colors"
                    >
                        <span className="text-2xl sm:text-3xl" aria-hidden="true">
                            {cat.icon}
                        </span>
                        <span
                            className="text-base sm:text-lg font-bold text-gray-900 dark:text-white tracking-tight"
                            style={{
                                fontFamily: "'Playfair Display', Georgia, serif",
                            }}
                        >
                            {t(`joinMentor_expertise_cat_${cat.id}`)}
                        </span>
                    </motion.div>
                ))}
            </div>

            <RevealOnScroll delay={0.4}>
                <p
                    className="mt-12 sm:mt-16 text-center italic text-gray-600 dark:text-white/70 max-w-2xl mx-auto"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                    {t("joinMentor_expertise_footer_part1")}{" "}
                    <span className="text-primary font-semibold not-italic">
                        {t("joinMentor_expertise_footer_emphasis")}
                    </span>
                </p>
            </RevealOnScroll>
        </SectionShell>
    );
}
